import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ArrowLeftCircle, Compass } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';
import { resolveMediaUrl, getVideoMimeType } from '../../lib/mediaUrl';
import { useHeroMedia } from '../../hooks/useHeroMedia';
import { categoryUrl } from '../../lib/activityUrls';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Category landing page - the porthole tile page.
 *
 * Driven entirely by data: a category with a `landing_path` (e.g.
 * "tours-charters-st-john-virgin-islands/private-boat-charters") renders its
 * sub-categories as the porthole tiles, in their admin order, each linking to that
 * sub-category's own listing page. Nothing about the tiles is hardcoded, so the other
 * main tour buttons only need a landing_path in Admin -> Activity Categories.
 *
 * The brass porthole frame is part of the (transparent) tile image itself, exactly like
 * the legacy site, so the title is simply centred over it.
 */
const TourCategoryLandingPage = () => {
  const location = useLocation();
  const path = location.pathname.replace(/^\/+|\/+$/g, '');
  const hero = useHeroMedia();
  const [state, setState] = useState({ status: 'loading', category: null, children: [] });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', category: null, children: [] });

    axios
      .get(`${API_URL}/api/public/tours-charters/landing/${path}`)
      .then((res) => {
        if (cancelled) return;
        setState({ status: 'found', category: res.data?.category || null, children: res.data?.children || [] });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'missing', category: null, children: [] });
      });

    return () => { cancelled = true; };
  }, [path]);

  const { status, category, children } = state;

  useEffect(() => {
    if (status !== 'found' || !category) return;
    const summary = category.description
      || `Every ${category.name.toLowerCase()} option on St John, U.S. Virgin Islands - pick a type below and book direct with the charter company.`;
    setSeoMetadata({
      title: category.name,
      description: summary,
      canonicalPath: `/${path}`,
      ogType: 'website',
      ogImage: category.image_url ? resolveMediaUrl(category.image_url) : undefined,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: category.name,
        description: summary,
        dateModified: category.updated_at || undefined,
        hasPart: children.map((child) => ({
          '@type': 'CollectionPage',
          name: child.name,
          url: `${window.location.origin}${categoryUrl(child)}`,
        })),
      },
    });
  }, [status, category, children, path]);

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white" data-testid="category-landing-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (status === 'missing' || !category) return <Navigate to="/" replace />;

  const heroVideo = resolveMediaUrl(category.hero_video_url) || hero.videoUrl;
  const heroVideoType = category.hero_video_url ? getVideoMimeType(category.hero_video_url) : hero.videoMimeType;
  const heroImage = resolveMediaUrl(category.hero_image_url) || hero.backgroundImageUrl;

  // The legacy layout keeps "Back to Home" in the middle cell of the last row.
  const backToHome = (
    <Link
      to="/"
      className="mx-auto flex w-full max-w-[340px] items-center justify-center gap-2 rounded-full border-2 border-emerald-500 bg-white px-6 py-3 text-base font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
      data-testid="category-landing-back-home"
    >
      <ArrowLeftCircle className="h-5 w-5" /> Back to Home
    </Link>
  );

  const tiles = children.map((child) => (
    <article key={child.id} data-testid={`landing-subcategory-${child.slug}`}>
      <Link to={`/${child.slug}`} className="group block" aria-label={child.name}>
        <div className="relative mx-auto aspect-square w-full max-w-[360px]">
          {child.image_url ? (
            <img
              src={resolveMediaUrl(child.image_url)}
              alt={child.name}
              loading="lazy"
              className="h-full w-full object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-[1.04]"
            />
          ) : (
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-gradient-to-br from-sky-400 to-blue-700">
              <Compass className="h-14 w-14 text-white/70" />
            </span>
          )}
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center px-[22%] text-center">
            <span className="text-lg font-semibold uppercase tracking-wide text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] sm:text-xl">
              {child.name}
            </span>
          </span>
        </div>
      </Link>
      {child.activity_count > 0 && (
        <p className="mt-1 text-center text-sm font-semibold text-slate-500" data-testid={`landing-subcategory-count-${child.slug}`}>
          {child.activity_count} {child.activity_count === 1 ? 'trip' : 'trips'}
        </p>
      )}
    </article>
  ));

  const cells = [...tiles];
  const inlineButton = children.length % 3 === 2;
  if (inlineButton) cells.splice(cells.length - 1, 0, <div key="back-home">{backToHome}</div>);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ================= HERO ================= */}
      <section className="relative flex min-h-[46vh] items-center justify-center overflow-hidden lg:min-h-[56vh]" data-testid="category-landing-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-100 via-white to-white" />
        {heroVideo ? (
          <video
            key={heroVideo}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={heroImage || undefined}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="category-landing-hero-video"
          >
            <source src={heroVideo} type={heroVideoType} />
          </video>
        ) : heroImage ? (
          <div className="absolute inset-0 h-full w-full bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }} />
        ) : null}
        <div className="absolute inset-0 bg-black/25" />

        <div className="relative z-10 w-full px-4 py-24 text-center">
          <h1 className="text-4xl font-extrabold italic tracking-tight text-white drop-shadow-[0_3px_10px_rgba(0,0,0,0.75)] sm:text-5xl lg:text-6xl"
            data-testid="category-landing-title">
            {category.name}
          </h1>
          {(category.subtitle || category.description) && (
            <h2 className="mt-4 text-xl font-semibold text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.75)] sm:text-2xl"
              data-testid="category-landing-subtitle">
              {category.subtitle || category.description}
            </h2>
          )}
        </div>
      </section>

      {/* ================= PORTHOLE TILES ================= */}
      <main className="mx-auto max-w-6xl px-4 py-14 lg:py-20">
        {children.length === 0 ? (
          <div className="py-10 text-center" data-testid="category-landing-empty">
            <p className="text-slate-600">
              This section is being set up. See every trip in this category in the meantime.
            </p>
            <Link
              to={category.legacy_path ? `/${category.legacy_path}` : `/activities/category/${category.slug}`}
              className="mt-6 inline-block rounded bg-blue-800 px-8 py-4 font-bold text-white transition-colors hover:bg-blue-900"
              data-testid="category-landing-all-link"
            >
              View all {category.name}
            </Link>
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 items-center gap-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10"
              data-testid="category-landing-grid">
              {cells}
            </section>
            {!inlineButton && <div className="mt-12">{backToHome}</div>}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default TourCategoryLandingPage;
